import _ from 'utils/@lodash';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchProjectEntities } from "api/Entities";
//import { setAutoFreeze } from 'immer';

// Fixes "Cannot assign to read only property" error message
// when modifying objects from Redux state directly.
// https://github.com/reduxjs/redux-toolkit/issues/424
//setAutoFreeze(false);

const initialState = {
    data: {
        views: {},
        projects: {},
        entities: {},
        pending: {},
        snapshots: {},
        comments: {},
        projectsRef: [],
        loaded: false,
        error: false,
    },
    entitiesCache: {},
    _tempEntities: [],
    update: 0
}

interface IProEntitiesCachePayload {
    id: string,
    data: any,
}
interface IProSetCachedDataPayload {
    data: any,
    id?: string,
}
interface IProSetProjectDataPayload {
    type: string,
    projectId: string,
    data: any,
}
interface IProGetProjectDataPayload {
    type: string,
    projectId: string
}
interface IProUpdateEntityToCachePayload {
    type: string,
    entity: any,
    projectId?: string,
}
interface IProDeleteEntityFromCachePayload {
    projectId: string,
    entityId: string,
}
interface IProUpdateDataPayload {
    action: string,
    type: string,
    projectId: string,
    updatedObject: any
}

const getProjectEntities = async (projectId: string) =>  await fetchProjectEntities(projectId);

const cacheSlice = createSlice({
	name: 'cache',
	initialState,
	reducers: {
		setEntitiesCache: (state: any, action: PayloadAction<IProEntitiesCachePayload>) => {
            return {
                ...state,
                update: Date.now(),
                entitiesCache: {
                    ...state.entitiesCache,
                    [action.payload.id]: action.payload.data
                }
            }
		},
		getCachedData: (state: any, action: PayloadAction<any>) => {
            return action.payload ? 
                        state.data[action.payload] :
                        {...state.data}
		},
		setCachedData: (state: any, action: PayloadAction<IProSetCachedDataPayload>) => {
            return action.payload.id ? 
                {
                    ...state,
                    update: Date.now(),
                    data: {
                        ...state.data,
                        [action.payload.id]: {
                            ...state.data[action.payload.data],
                            ...action.payload.data
                        }
                    }
                } : {
                    ...state,
                    update: Date.now(),
                    data: { ...action.payload.data }
                }
		},
		setTempEntities: (state: any, action: PayloadAction<any>) => {
            return {
                ...state,
                update: Date.now(),
                _tempEntities:  action.payload
            };
		},
		getTempEntities: (state: any) => {
            return {
                ...state._tempEntities
            };
		},
		setProjectData: (state: any, action: PayloadAction<IProSetProjectDataPayload>) => {
			const { type, projectId, data } = action.payload
            return {
                ...state,
                update: Date.now(),
                data: {
                    ...state.data,
                    [type]: {
                        ...state.data[type],
                        [projectId]: data
                    }
                }
            };
		},
		getProjectData: (state: any, action: PayloadAction<IProGetProjectDataPayload>) => {
			const { type, projectId } = action.payload
            return _.has(state.data, [type, projectId]) ? {
                ...state.data[type][projectId]
            } : null;
		},
		updateEntityToCache: (state: any, action: PayloadAction<IProUpdateEntityToCachePayload>) => {
			const { type, projectId, entity } = action.payload

            let _state;
            switch(type) {
                case 'views':
                    _state =  {
                        ...state,
                        data: {
                            ...state.data,
                            [type]: {
                                ...state.data[type],
                                ...( _.has(entity, 'ID') ? {
                                        [entity.ID] : {
                                            ...(state.data[type][entity.ID] || {}),
                                            ...entity
                                        }
                                } : {})
                            }
                        }
                    }
                    break;
                case 'projects':
                    _state =  {
                        ...state,
                        data: {
                            ...state.data,
                            [type]: {
                                ...state.data[type],
                                ...(projectId ? {
                                        [projectId] : {
                                            ...entity
                                        }
                                } : {})
                            }
                        }
                    }
                    break;
                default:
                    _state =  {
                        ...state,
                        data: {
                            ...state.data,
                            [type]: {
                                ...state.data[type],
                                ...(projectId ? {
                                    [projectId] : {
                                        ...(state.data[type][projectId] || {}),
                                        ...( _.has(entity, 'ID') ? {
                                            [entity.ID] : {
                                                ...(state.data[type][projectId][entity.ID] || {}),
                                                ...entity
                                            }
                                        } : {})
                                    }
                                } : {})
                            }
                        }
                    }
            }

            _state.update = Date.now();

            return _state;
		},
        updateViewsCache: (state: any, action: PayloadAction<any>) => {
            return {
                ...state,
                update: Date.now(),
                data: {
                    ...state.data,
                    views: {
                        ...state.data.views,
                        ...( _.has(action.payload, 'ID') ? {
                            [action.payload.ID] : {
                                ...(state.data.views[action.payload.ID] || {}),
                                ...action.payload
                            }
                        } : {})
                    }
                }
            };
		},
        deleteEntityFromCache: (state: any, action: PayloadAction<IProDeleteEntityFromCachePayload>) => {
            const { projectId, entityId } = action.payload;
            return {
                ...state,
                update: Date.now(),
                data: {
                    ...state.data,
                    entities: {
                        ...state.data.entities,
                        ...( _.has(state.data.entities, projectId) ? {
                            [state.data.entities[projectId]] : Object.fromEntries(
                                Object.entries(state.data.entities[projectId]).filter(
                                   ([key, _]) => key !== entityId
                                )
                            )
                        } : {})
                    }
                }
            };
		},
        deleteProjectFromCache: (state: any, action: PayloadAction<string>) => {
            return {
                ...state,
                update: Date.now(),
                data: {
                    ...state.data,
                    entities: Object.fromEntries(
                        Object.entries(state.data.entities).filter(
                           ([key, _]) => key !== action.payload
                        )
                    ),
                    projects: Object.fromEntries(
                        Object.entries(state.data.projects).filter(
                           ([key, _]) => key !== action.payload
                        )
                    ),
                    projectsRef: state.data.projectsRef.filter((item:any) => item.project_ref !== action.payload)
                }
            };
		},
        deleteViewFromCache: (state: any, action: PayloadAction<string>) => {
            return {
                ...state,
                update: Date.now(),
                data: {
                    ...state.data,
                    views: Object.fromEntries(
                        Object.entries(state.data.views).filter(
                           ([key, _]) => key !== action.payload
                        )
                    )
                }
            };
		},
        updateData: (state: any, action: PayloadAction<IProUpdateDataPayload>) => {
            const { 
                action:reason,
                type,
                projectId,
                updatedObject,
            } = action.payload;

            const cachedObject = state[type][projectId];
            const pending = state["pending"];
            const pendingArray = Object.values(pending);
            let _state;

            switch (type) {
                case "entities": {
                    switch (reason) {
                        case "create": {
                            _state = {
                                ...state,
                                ...(cachedObject && _.has(updatedObject, 'ID')  ? {
                                    data: {
                                        ...state.data,
                                        [type]: {
                                            ...state.data[type],
                                            [projectId]: {
                                                ...(state.data[type][projectId] || {}),
                                                [updatedObject.ID]: { ...updatedObject }
                                            }
                                        },
                                        ...(!pendingArray.find(
                                                (element: any) =>
                                                    element.view_type === "folder" &&
                                                    element.ID === updatedObject.ID &&
                                                    element.name === updatedObject.name
                                            ) ? {
                                                pending: {
                                                    ...state.pending,
                                                    [updatedObject.ID]: { ...updatedObject }
                                                }
                                        } : {}),
                                    }
                                } : {})
                            }
                            break;
                        }
                        case "update": {
                            if (cachedObject) {
                                state[type][projectId][updatedObject.ID] = updatedObject;
                                //console.log("state:", state);
                                setCachedData(state);
                            }
                            _state = {
                                ...state,
                                ...(cachedObject && _.has(updatedObject, 'ID')  ? {
                                    data: {
                                        ...state.data,
                                        [type]: {
                                            ...state.data[type],
                                            [projectId]: {
                                                ...(state.data[type][projectId] || {}),
                                                [updatedObject.ID]: { ...updatedObject }
                                            }
                                        }
                                    }

                                } : {})
                            }
                            break;
                        }
                        case "updateEntity": {
                            _state = {
                                ...state,
                                ...(cachedObject && _.has(updatedObject, 'ID') && _.has(state.data, [type, projectId, updatedObject.ID])  ? {
                                    data: {
                                        ...state.data,
                                        [type]: {
                                            ...state.data[type],
                                            [projectId]: {
                                                ...state.data[type][projectId],
                                                [updatedObject.ID]: { ...updatedObject }
                                            }
                                        },
                                        pending: {
                                            ...state.pending,
                                            [updatedObject.ID]: { ...updatedObject }
                                        }
                                    }
                                } : {})
                            }
                            break;
                        }
                        case "save": {
                            _state = {
                                ...state,
                                ...(cachedObject && _.has(updatedObject, 'ID') ? {
                                    data: {
                                        ...state.data,
                                        [type]: {
                                            ...state.data[type],
                                            [projectId]: {
                                                ...(state.data[type][projectId] || {}),
                                                [updatedObject.ID]: { ...updatedObject }
                                            }
                                        },
                                        pending: {
                                            ...state.pending,
                                            [updatedObject.ID]: { ...updatedObject }
                                        }
                                    }
                                } : {})
                            }
                            break;
                        }
                        case "delete": {
                            _state = {
                                ...state,
                                ...(cachedObject && _.has(updatedObject, 'ID') ? {
                                    data: {
                                        ...state.data,
                                        [type]: {
                                            ...state.data[type],
                                            [projectId]: Object.fromEntries(
                                                Object.entries(state.data[type][projectId]).filter(
                                                   ([key, _]) => key !== updatedObject.ID
                                                )
                                            )
                                        },
                                        pending: {
                                            ...state.pending,
                                            [updatedObject.ID]: { 
                                                ...updatedObject, 
                                                action: "remove"
                                            }
                                        }
                                    }
                                } : {})
                            }
                            break;
                        }
                    }
                    break;
                }
                case "views": {
                    switch (reason) {
                        case "create": {
                            _state = {
                                ...state,
                                ...(_.has(updatedObject, 'ID') && !cachedObject  ? {
                                    data: {
                                        ...state.data,
                                        [type]: {
                                            ...state.data[type],
                                            [projectId]: { ...updatedObject }
                                        },
                                        pending: {
                                            ...state.pending,
                                            [updatedObject.ID]: { 
                                                ...updatedObject,
                                                action: "add"
                                            }
                                        }
                                    }
                                } : {})
                            }
                            break;
                        }
                        case "updateIcon": {
                            _state = {
                                ...state,
                                ...(updatedObject  ? {
                                    data: {
                                        ...state.data,
                                        [type]: {
                                            ...state.data[type],
                                            [projectId]: { 
                                                ...state.data[type][projectId],
                                                thumb: updatedObject 
                                            }
                                        }
                                    }
                                } : {})
                            }
                            break;
                        }
                        case "update": {
                            _state = {
                                ...state,
                                ...(_.has(state.data, [type, projectId])  ? {
                                    data: {
                                        ...state.data,
                                        [type]: {
                                            ...state.data[type],
                                            [projectId]: { ...updatedObject }
                                        }
                                    }
                                } : {})
                            }
                            break;
                        }
                        case "save": {
                            _state = {
                                ...state,
                                data: {
                                    ...state.data,
                                    [type]: {
                                        ...state.data[type],
                                        [projectId]: { ...updatedObject }
                                    },
                                    pending: {}
                                }
                            }
                            break;
                        }
                        case "delete": {
                            _state = {
                                ...state,
                                ...(_.has(state.data, [type, projectId]) ? {
                                    data: {
                                        ...state.data,
                                        [type]: Object.fromEntries(
                                                    Object.entries(state.data[type]).filter(
                                                    ([key, _]) => key !== projectId
                                                )),
                                        pending:  Object.fromEntries(
                                                Object.entries(state.pending).filter(
                                                ([key, _]) => key !== projectId
                                            ))
                                    }
                                } : {})
                            }
                            break;
                        }
                    }
                    break;
                }
                case "projects": {
                    switch (reason) {
                        case "create": {
                            _state = {
                                ...state,
                                data: {
                                    ...state.data,
                                    [type]: {
                                        ...state.data[type],
                                        [projectId]: { ...updatedObject }
                                    },
                                    entities: {
                                        ...state.data.entities,
                                        //...(!_.has(state.entities, projectId)   ? {
                                            [projectId]: { ...( getProjectEntities(projectId) )}
                                        //} : {})
                                    }
                                }
                            }
                            break;
                        }
                        case "delete": {
                            _state = {
                                ...state,
                                data: {
                                    ...state.data,
                                    [type]: Object.fromEntries(
                                                Object.entries(state.data[type]).filter(
                                                ([key, _]) => key !== projectId
                                            )),
                                    entities: Object.fromEntries(
                                        Object.entries(state.data.entities).filter(
                                        ([key, _]) => key !== projectId
                                    ))
                                }
                            }
                            break;
                        }
                    }
                    break;
                }
                case "pending": {
                    const view: any = pendingArray.find((entry: any) => entry.ID === projectId);

                    _state = {
                        ...state,
                        data: {
                            ...state.data,
                            ...(view ? {
                                pending: {
                                    ...view,
                                    meta: { ...updatedObject }
                                }
                        } : {}),
                        }
                    }
                    break;
                }
                case "snapshots": {
                    switch (reason) {
                        case "add": {
                            _state = {
                                ...state,
                                data: {
                                    ...state.data,
                                    [type]: {
                                        ...state.data[type],
                                        [projectId]: !cachedObject ? [updatedObject] : [
                                            ...Object.values(cachedObject),
                                            { ...updatedObject }
                                        ]
                                    }
                                }
                            }
                            break;
                        }
                        case "save": {
                            //saving snapshots
                            _state = {
                                ...state
                            }
                            break;
                        }
                    }
                    break;
                }
                case "comments": {
                    switch (reason) {
                        case "save": {
                            state[type][projectId] = updatedObject;
                            setCachedData(state);
                            _state = {
                                ...state,
                                data: {
                                    ...state.data,
                                    [type]: {
                                        ...state.data[type],
                                        [projectId]: updatedObject
                                    }
                                }
                            }
                            break;
                        }
                    }
                    break;
                }
            }

            _state.update = Date.now();

            return _state;
		}
	},
	extraReducers: (builder) => {
       
	}
});

export const { 
	setEntitiesCache,
    getCachedData,
    setCachedData,
    setTempEntities,
    getTempEntities,
    setProjectData,
    getProjectData,
    updateEntityToCache,
    updateViewsCache,
    deleteEntityFromCache,
    deleteProjectFromCache,
    updateData,
    deleteViewFromCache
 } = cacheSlice.actions;

export type CacheState = ReturnType<typeof cacheSlice.reducer>;
export default cacheSlice.reducer;
